import React, { useEffect, useState } from "react";
import {
  Table,
  Space,
  Button,
  Typography,
  Tag,
  Card,
  Modal,
  Select,
} from "antd";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { getSlowOrders, getAdmins } from "../api/api";
import Spinner from "../components/Spinner";
import moment from "moment";
import { ClockCircleOutlined, BarChartOutlined } from "@ant-design/icons";
import Status from "../components/Status";
import SlowGraph from "../components/slowGraph";
import { useSelector } from "react-redux";

const { Column } = Table;
const { Text } = Typography;
const { Option } = Select;

export default function SlowOrders() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const history = useNavigate();
  const location = useLocation();

  const config = useSelector((state) => state.config?.config);

  // Extract stage and status parameters from URL query params
  const searchParams = new URLSearchParams(location.search);
  const stage = searchParams.get("stage");
  const activeStatus = searchParams.get("status");
  const agingParam = searchParams.get("aging");
  const adminID = useSelector((state) => state.auth.profile?._id);


  useEffect(() => {
    setLoading(true);
    window.scrollTo({ top: 0 });

    const mFilter = {};
    if (stage) {
      mFilter.stage = stage;
    }
    if (adminID) {
      mFilter.cn_manager = adminID;
    }

    getSlowOrders((mData) => {
      setData(mData);
      setLoading(false);
    }, mFilter);
  }, [stage, adminID]);

  // Extract orders list from the data response
  let ordersList = data?.orders || [];

  // Filter orders locally on client-side if a status filter is active
  if (activeStatus) {
    ordersList = ordersList.filter((order) => {
      const orderStatusId = order.status?._id !== undefined ? order.status._id : order.status;
      return String(orderStatusId) === activeStatus;
    });
  }

  // Filter orders locally on client-side if aging filter is active
  if (agingParam) {
    ordersList = ordersList.filter((order) => {
      const slowDays = order.day_count;
      if (slowDays === undefined || slowDays === null) return false;
      if (agingParam === "4-7 Days") return slowDays >= 4 && slowDays <= 7;
      if (agingParam === "7-15 Days") return slowDays > 7 && slowDays <= 15;
      if (agingParam === "16-30 Days") return slowDays > 15 && slowDays <= 30;
      if (agingParam === "31-60 Days") return slowDays > 30 && slowDays <= 60;
      if (agingParam === "61-90 Days") return slowDays > 60 && slowDays <= 90;
      if (agingParam === "91-180 Days") return slowDays > 90 && slowDays <= 180;
      if (agingParam === "180+ Days") return slowDays > 180 && slowDays <= 365;
      if (agingParam === "365+ Days") return slowDays > 365 && slowDays <= 730;
      if (agingParam === "730+ Days") return slowDays > 730;
      return true;
    });
  }

  const agingGroups = [
    "4-7 Days",
    "7-15 Days",
    "16-30 Days",
    "31-60 Days",
    "61-90 Days",
    "91-180 Days",
    "180+ Days",
    "365+ Days",
    "730+ Days",
  ];

  const getAgingCount = (group) => {
    let list = data?.orders || [];
    if (activeStatus) {
      list = list.filter((order) => {
        const orderStatusId = order.status?._id !== undefined ? order.status._id : order.status;
        return String(orderStatusId) === activeStatus;
      });
    }
    return list.filter((order) => {
      const slowDays = order.day_count;
      if (slowDays === undefined || slowDays === null) return false;
      if (group === "4-7 Days") return slowDays >= 4 && slowDays <= 7;
      if (group === "7-15 Days") return slowDays > 7 && slowDays <= 15;
      if (group === "16-30 Days") return slowDays > 15 && slowDays <= 30;
      if (group === "31-60 Days") return slowDays > 30 && slowDays <= 60;
      if (group === "61-90 Days") return slowDays > 60 && slowDays <= 90;
      if (group === "91-180 Days") return slowDays > 90 && slowDays <= 180;
      if (group === "180+ Days") return slowDays > 180 && slowDays <= 365;
      if (group === "365+ Days") return slowDays > 365 && slowDays <= 730;
      if (group === "730+ Days") return slowDays > 730;
      return false;
    }).length;
  };

  // Stats calculation
  const totalCount = data?.count !== undefined ? data.count : ordersList.length;

  const statsStyle = {
    cursor: "pointer",
    textTransform: "capitalize",
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
    color: "black",
    padding: "1rem",
    backgroundColor: "white",
    width: "220px",
    justifyContent: "center",
    borderRadius: "6px",
    boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
  };

  const getCardStyle = (stageKey) => {
    const isSelected = stageKey ? stage === stageKey : !stage;
    return {
      ...statsStyle,
      border: isSelected ? "2px solid #D1412A" : "2px solid transparent",
      backgroundColor: isSelected ? "#fff2f0" : "white",
    };
  };

  const handleStageClick = (stageKey) => {
    const params = new URLSearchParams(location.search);
    if (stageKey) {
      params.set("stage", stageKey);
    } else {
      params.delete("stage");
    }
    history(params.toString() ? `?${params.toString()}` : "");
  };

  const handleStatusClick = (statusId) => {
    const params = new URLSearchParams(location.search);
    if (!statusId) {
      params.delete("status");
    } else {
      const currentStatus = params.get("status");
      if (currentStatus === String(statusId)) {
        params.delete("status");
      } else {
        params.set("status", statusId);
      }
    }
    history(params.toString() ? `?${params.toString()}` : "");
  };

  const handleAgingChange = (val) => {
    const params = new URLSearchParams(location.search);
    if (val) {
      params.set("aging", val);
    } else {
      params.delete("aging");
    }
    history(params.toString() ? `?${params.toString()}` : "");
  };

  const handleReset = () => {
    history(location.pathname);
  };

  return (
    <div>
      {/* Title Header */}
      <div
        style={{
          display: "flex",
          marginBottom: "1rem",
          marginTop: 8,
          backgroundColor: "#fff",
          justifyContent: "space-between",
          padding: 12,
          alignItems: "center",
          borderRadius: 6,
          flexWrap: "wrap",
          gap: 12,
        }}
        className="tagTab"
      >
        <span style={{ fontSize: 16, fontWeight: "bold" }}>Slow Orders Report</span>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <Button
            type="primary"
            icon={<BarChartOutlined />}
            onClick={() => setIsModalVisible(true)}
          >
            View Graph
          </Button>
        </div>
      </div>

      {/* Stats Widgets */}
      <div
        style={{
          display: "flex",
          marginBottom: "1rem",
          flexWrap: "wrap",
          gap: 8,
        }}
        className="statusGrid report"
      >
        <div style={getCardStyle(null)} onClick={() => handleStageClick(null)}>
          <Text style={{ fontSize: 14, color: "gray", textAlign: "center" }}>
            Total Slow Orders
          </Text>
          <Text style={{ fontSize: 18, fontWeight: "bold", color: "#D1412A" }}>
            {(data?.count !== undefined ? data.count : totalCount).toLocaleString()}
          </Text>
        </div>

        <div style={getCardStyle("purchase")} onClick={() => handleStageClick("purchase")}>
          <Text style={{ fontSize: 14, color: "gray", textAlign: "center" }}>
            Purchase
          </Text>
          <Text style={{ fontSize: 18, fontWeight: "bold", color: "#1890ff" }}>
            {(data?.stage_stats?.purchase || 0).toLocaleString()}
          </Text>
        </div>

        <div style={getCardStyle("supplier")} onClick={() => handleStageClick("supplier")}>
          <Text style={{ fontSize: 14, color: "gray", textAlign: "center" }}>
            Supplier
          </Text>
          <Text style={{ fontSize: 18, fontWeight: "bold", color: "#722ed1" }}>
            {(data?.stage_stats?.supplier || 0).toLocaleString()}
          </Text>
        </div>

        <div style={getCardStyle("china_warehouse")} onClick={() => handleStageClick("china_warehouse")}>
          <Text style={{ fontSize: 14, color: "gray", textAlign: "center" }}>
            China Warehouse
          </Text>
          <Text style={{ fontSize: 18, fontWeight: "bold", color: "#fa8c16" }}>
            {(data?.stage_stats?.china_warehouse || 0).toLocaleString()}
          </Text>
        </div>

        <div style={getCardStyle("shipment")} onClick={() => handleStageClick("shipment")}>
          <Text style={{ fontSize: 14, color: "gray", textAlign: "center" }}>
            Shipment
          </Text>
          <Text style={{ fontSize: 18, fontWeight: "bold", color: "#13c2c2" }}>
            {(data?.stage_stats?.shipment || 0).toLocaleString()}
          </Text>
        </div>

        <div style={getCardStyle("bd_warehouse")} onClick={() => handleStageClick("bd_warehouse")}>
          <Text style={{ fontSize: 14, color: "gray", textAlign: "center" }}>
            BD Warehouse
          </Text>
          <Text style={{ fontSize: 18, fontWeight: "bold", color: "#52c41a" }}>
            {(data?.stage_stats?.bd_warehouse || 0).toLocaleString()}
          </Text>
        </div>
      </div>

      {/* Status Stats Badges */}
      {data?.status_stats && (
        <Card style={{ marginBottom: ".5rem" }} title={<span style={{ fontSize: 14, fontWeight: "bold" }}>Status</span>}
          extra={
            <Space>
              <Select
                placeholder="Select Days"
                style={{ width: 180 }}
                allowClear
                showSearch
                optionFilterProp="children"
                value={agingParam || undefined}
                onChange={handleAgingChange}
              >
                {agingGroups.map((group) => {
                  const count = getAgingCount(group);
                  return (
                    <Option key={group} value={group}>
                      {group} ({count})
                    </Option>
                  );
                })}
              </Select>
              <Button type="primary" danger onClick={handleReset}>
                Reset Filters
              </Button>
            </Space>
          }
        >
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 12px", padding: "8px 4px" }}>
            {/* "All" Status Tag */}
            {(() => {
              const totalStatusCount = data.status_stats.reduce((acc, curr) => acc + (curr.count || 0), 0);
              return (
                <Tag
                  onClick={() => handleStatusClick(null)}
                  style={{
                    margin: 0,
                    padding: "4px 10px",
                    fontSize: "13px",
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    borderColor: !activeStatus ? "#D1412A" : "#e5e5e5",
                    color: !activeStatus ? "#D1412A" : "#595959",
                    backgroundColor: !activeStatus ? "#fff2f0" : "#ffffff",
                    fontWeight: !activeStatus ? "600" : "500",
                    borderRadius: "4px",
                    userSelect: "none",
                  }}
                >
                  All
                  <span
                    style={{
                      marginLeft: 8,
                      backgroundColor: totalStatusCount > 0 ? "#D1412A" : "#f0f0f0",
                      color: totalStatusCount > 0 ? "#ffffff" : "#8c8c8c",
                      padding: "0 6px",
                      borderRadius: "10px",
                      fontSize: "11px",
                      lineHeight: "16px",
                      minWidth: "18px",
                      textAlign: "center",
                      display: "inline-block",
                      fontWeight: "bold",
                    }}
                  >
                    {totalStatusCount}
                  </span>
                </Tag>
              );
            })()}

            {data.status_stats?.filter(el => el.count > 0).map((statusItem) => {
              const isSelected = activeStatus === String(statusItem._id);
              return (
                <Tag
                  key={statusItem._id}
                  onClick={() => handleStatusClick(statusItem._id)}
                  style={{
                    margin: 0,
                    padding: "4px 10px",
                    fontSize: "13px",
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    borderColor: isSelected ? "#D1412A" : "#e5e5e5",
                    color: isSelected ? "#D1412A" : "#595959",
                    backgroundColor: isSelected ? "#fff2f0" : "#ffffff",
                    fontWeight: isSelected ? "600" : "500",
                    borderRadius: "4px",
                    userSelect: "none",
                  }}
                >
                  {statusItem.title}
                  <span
                    style={{
                      marginLeft: 8,
                      backgroundColor: statusItem.count > 0 ? "#D1412A" : "#f0f0f0",
                      color: statusItem.count > 0 ? "#ffffff" : "#8c8c8c",
                      padding: "0 6px",
                      borderRadius: "10px",
                      fontSize: "11px",
                      lineHeight: "16px",
                      minWidth: "18px",
                      textAlign: "center",
                      display: "inline-block",
                      fontWeight: "bold",
                    }}
                  >
                    {statusItem.count}
                  </span>
                </Tag>
              );
            })}
          </div>
        </Card>
      )}

      {/* Filter Card */}
      {/* <Card style={{ marginBottom: ".5rem" }} size="small">
        <Row gutter={[16, 16]}>
          <Col md={{ span: 8 }}>
            <Text style={{ fontSize: "14px" }}>Search Customer (Phone / Email)</Text>
            <Search
              placeholder="Enter Phone or Email"
              onSearch={(value) => {
                history(`/report/advanced/slow-orders?page=1`);
                let prev = { ...filter };
                if (value) {
                  if (value.includes("@")) {
                    prev.email = value;
                    delete prev.phone;
                  } else {
                    prev.phone = value;
                    delete prev.email;
                  }
                } else {
                  delete prev.phone;
                  delete prev.email;
                }
                setFilter(prev);
              }}
              allowClear
              enterButton
              style={{ width: "100%", marginTop: "0.25rem" }}
            />
          </Col>

          <Col md={{ span: 8 }}>
            <Text style={{ fontSize: "14px" }}>Order / Code ID</Text>
            <Search
              placeholder="Enter Order Code ID"
              onSearch={(value) => {
                history(`/report/advanced/slow-orders?page=1`);
                let prev = { ...filter };
                if (value) {
                  prev.code = value;
                } else {
                  delete prev.code;
                }
                setFilter(prev);
              }}
              allowClear
              enterButton
              style={{ width: "100%", marginTop: "0.25rem" }}
            />
          </Col>

          <Col md={{ span: 8 }}>
            <Text style={{ fontSize: "14px" }}>Date Range</Text>
            <div style={{ marginTop: "0.25rem" }}>
              <RangePicker
                style={{ width: "100%" }}
                format="YYYY/MM/DD"
                onChange={(dates) => {
                  history(`/report/advanced/slow-orders?page=1`);
                  let prev = { ...filter };
                  if (dates && dates.length === 2) {
                    prev.start_date = dates[0].format("YYYY-MM-DD");
                    prev.end_date = dates[1].format("YYYY-MM-DD");
                  } else {
                    delete prev.start_date;
                    delete prev.end_date;
                  }
                  setFilter(prev);
                }}
              />
            </div>
          </Col>
        </Row>
      </Card> */}

      {/* Table Results */}
      {!loading && data ? (
        <Table
          dataSource={ordersList}
          bordered
          rowKey={(record) => record._id || record.code}
          pagination={{ pageSize: 50, showSizeChanger: false }}
          onChange={() => {
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
        >

          <Column
            title="Date"
            key="date"
            render={(text, record) => (
              <div style={{ fontSize: "13px" }}>
                <p>{moment(new Date(record.createdAt)).format("DD/MM/YYYY")}</p>
                <p style={{ color: "gray" }}>{moment(new Date(record.createdAt)).format("hh:mm A")}</p>
              </div>
            )}
          />

          <Column
            title="Order"
            key="code"
            render={(text, record) => (
              <div style={{ textTransform: "uppercase" }}>
                <Link
                  to={`https://shahil.skybuybd.com/order/${record.code}`}
                  target="_blank"
                >
                  #{record.code ? record.code : record._id}
                </Link>
              </div>
            )}
          />



          {/* <Column
            title="Customer"
            key="customer"
            render={(text, record) => (
              <CustomerName
                data={record.customer}
                country={
                  record?.delivery?.address?.country !== "BD"
                    ? record?.delivery?.address?.country
                    : null
                }
                withBadge={true}
              />
            )}
          /> */}

          <Column
            title="Days"
            align="center"
            key="slow_days"
            render={(text, record) => {
              const slowDays = record.day_count
              return (
                <Tag color={slowDays > 15 ? "red" : slowDays > 7 ? "orange" : "blue"}>
                  <ClockCircleOutlined style={{ marginRight: 4 }} />
                  {slowDays} Days
                </Tag>
              );
            }}
          />

          <Column
            title="Status"
            key="status"
            align="center"
            render={(text, record) => (
              <Status
                name={record.status?.title || "Pending"}
                type="order_status"
                smallFont
              />
            )}
          />

        </Table>
      ) : (
        <div style={{ textAlign: "center", padding: "3rem" }}>
          <Spinner />
        </div>
      )}

      <Modal
        title="Slow Orders Visualization"
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={1000}
        destroyOnClose
      >
        <SlowGraph
          stageStats={data?.stage_stats}
          statusStats={data?.status_stats}
          orders={data?.orders}
        />
      </Modal>
    </div>
  );
}
